"use client";

import { cn } from "@marketing-sites/utils/cn";
import { stripPiiParams } from "@marketing-sites/utils/strip-pii-params";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsLinkClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { useLocalizeHref } from "@marketing-sites/l10n/providers/localize-href-context";
import { LinkProps as NextLinkProps } from "next/link";
import NextLink from "next/link";
import { isCrossAppHref } from "@marketing-sites/utils/cross-app-href";

type NextLinkPropsWithTargetHandling = NextLinkProps &
  Partial<Pick<HTMLAnchorElement, "target" | "rel">>;

export interface LinkBaseProps extends NextLinkPropsWithTargetHandling {
  children?: React.ReactNode;
  className?: string;
  id?: string;
  "aria-label"?: string;
  "aria-current"?: React.AriaAttributes["aria-current"];
  analyticsLabel?: string;
}

export type LinkProps = LinkBaseProps &
  (
    | { disableAnalytics: true; analyticsId?: never }
    | { disableAnalytics?: false; analyticsId: string }
  );

export function Link({
  onClick,
  children,
  className,
  disableAnalytics = false,
  analyticsLabel,
  analyticsId,
  href: rawHref,
  ...props
}: LinkProps) {
  const localizeHref = useLocalizeHref();
  const isCrossApp = typeof rawHref === "string" && isCrossAppHref(rawHref);
  const href = typeof rawHref === "string" ? localizeHref(rawHref) : rawHref;

  const handleClick: typeof onClick = (event) => {
    const location = getAnalyticsRegion(event.currentTarget) ?? "Unknown";
    const text =
      analyticsLabel ?? (typeof children === "string" ? children : "Link");
    const destination = typeof href === "string" ? href : String(href);

    if (analyticsId) {
      triggerAnalyticsLinkClick({
        text,
        location,
        destination: stripPiiParams(destination),
        analyticsId,
      });
    }

    onClick?.(event);
  };

  const sharedProps = {
    onClick: disableAnalytics ? onClick : handleClick,
    className: cn(
      className,
      "focus-visible:outline-2 focus-visible:outline-offset-2",
    ),
  };

  if (isCrossApp) {
    const {
      prefetch: _prefetch,
      replace: _replace,
      scroll: _scroll,
      shallow: _shallow,
      locale: _locale,
      passHref: _passHref,
      legacyBehavior: _legacyBehavior,
      onNavigate: _onNavigate,
      ...anchorProps
    } = props;
    return (
      <a href={href as string} {...anchorProps} {...sharedProps}>
        {children}
      </a>
    );
  }

  return (
    <NextLink href={href} {...sharedProps} {...props}>
      {children}
    </NextLink>
  );
}
